/**
 * Hjälpinnehållet.
 *
 * Ligger som data och inte som JSX av tre skäl: det går att söka i, det går
 * att djuplänka till, och det går att kontrollera med ett test att varje nivå
 * faktiskt har innehåll. En hjälpsida där halva texten försvunnit i en
 * redigering ser likadan ut som en där allt är på plats.
 *
 * Ordningen inom varje nivå är inte alfabetisk utan följer i vilken ordning
 * man stöter på sakerna. Den som söker hjälp har oftast fastnat på det
 * tredje steget, inte på det första.
 */

import type { Role } from '@shared/model'
import type { FeatureKey, Features } from '@shared/features'

/**
 * Nivåerna i hjälpen.
 *
 * "Förälder" är ingen roll i systemet – det är en köpare som beställt häften
 * för att sälja vidare. Den skiljs ändå ut här, eftersom det är två helt
 * olika situationer att vara den som använder ett häfte och den som ska sälja
 * fyra.
 */
export type HelpLevel = 'buyer' | 'seller' | 'teamLeader' | 'admin' | 'superadmin'

export const HELP_LEVEL_LABELS: Record<HelpLevel, string> = {
  buyer: 'Använda häftet',
  seller: 'Sälja häften',
  teamLeader: 'Lagledare',
  admin: 'Administratör',
  superadmin: 'Superadmin',
}

export const HELP_LEVEL_INTROS: Record<HelpLevel, string> = {
  buyer: 'Du har ett häfte och vill använda rabatterna.',
  seller: 'Du har köpt häften som ditt barn ska sälja vidare.',
  teamLeader: 'Du håller reda på lagets försäljning och betalningar.',
  admin: 'Du lägger upp perioder, företag, erbjudanden och koder.',
  superadmin: 'Du sköter roller, testdata, backup och rättelser.',
}

export interface HelpTopic {
  /** Används i länken: /hjalp#aktivera-kod */
  id: string
  level: HelpLevel
  question: string
  answer: string
  /** Numrerad gång för de saker som faktiskt är en procedur. */
  steps?: string[]
}

export const HELP_TOPICS: HelpTopic[] = [
  /* ---------------------------------------------------------------- *
   * Använda häftet
   * ---------------------------------------------------------------- */
  {
    id: 'aktivera-kod',
    level: 'buyer',
    question: 'Hur aktiverar jag mitt häfte?',
    answer:
      'Koden står på lappen du köpt, eller i mailet om du köpt digitalt. Den ser ut som LB-XXXX-XXXX. Aktiveringen knyter häftet till ditt konto, och en kod kan bara aktiveras en gång.',
    steps: [
      'Gå till Mina koder.',
      'Skriv in koden. Stora och små bokstäver spelar ingen roll, och bindestrecken behöver du inte skriva.',
      'Häftet öppnas direkt och alla erbjudanden blir tillgängliga.',
    ],
  },
  {
    id: 'anvanda-rabatt',
    level: 'buyer',
    question: 'Hur använder jag en rabatt i butiken?',
    answer:
      'Öppna erbjudandet och håll in knappen i tre sekunder medan personalen ser skärmen. Då slår en stämpel ned med datum, klockslag och ett kvittonummer. Rabatten är förbrukad direkt efteråt.',
  },
  {
    id: 'angra-rabatt',
    level: 'buyer',
    question: 'Jag råkade markera en rabatt som använd. Kan jag ångra?',
    answer:
      'Nej. Det fungerar precis som med pappershäftet: har kupongen väl rivits ur går den inte att klistra tillbaka, och ingen kan i efterhand avgöra om den användes i butiken eller inte. Därför räknas en rabatt du hållit in alltid som förbrukad. Håll bara in knappen när du står vid kassan och personalen tittar.',
  },
  {
    id: 'flera-haften',
    level: 'buyer',
    question: 'Jag har två häften. Får jag använda varje rabatt två gånger?',
    answer:
      'Ja. Varje erbjudande kan användas en gång per häfte du äger. I appen står det "2 kvar" på erbjudandet, och räknaren minskar för varje inlösen.',
  },
  {
    id: 'offline',
    level: 'buyer',
    question: 'Fungerar häftet utan internet?',
    answer:
      'Du kan läsa och visa alla erbjudanden offline. Att markera en rabatt som använd kräver däremot uppkoppling – annars skulle samma rabatt kunna användas flera gånger.',
  },
  {
    id: 'personalen-fragar',
    level: 'buyer',
    question: 'Personalen undrar hur de ska slå in rabatten',
    answer:
      'På vissa erbjudanden står en instruktion till kassören, till exempel vilket PLU som ska slås. Den visas både på erbjudandet och på kvittot efter inlösen, så du kan visa skärmen.',
  },
  {
    id: 'haftet-gar-ut',
    level: 'buyer',
    question: 'Vad händer när perioden tar slut?',
    answer:
      'Erbjudandena slutar gå att använda, men häftet ligger kvar på ditt konto och du kan se hur mycket du sparade under perioden.',
  },
  {
    id: 'byta-enhet',
    level: 'buyer',
    question: 'Kan jag använda häftet på flera enheter?',
    answer:
      'Ja. Häftet hör till ditt konto, inte till telefonen. Logga in med samma e-postadress på en annan enhet så finns allt där, inklusive vad du redan använt.',
  },
  {
    id: 'byta-adress',
    level: 'buyer',
    question: 'Jag har bytt e-postadress. Vad händer med mitt häfte?',
    answer:
      'Fortsätt logga in med den gamla adressen – häftet hör till det konto du aktiverade med, och det fungerar även om adressen inte längre är en brevlåda du läser. Adressen går inte att byta, och skapar du ett nytt konto följer häftet inte med: en kod kan bara aktiveras en gång. Se till att komma ihåg lösenordet, för en återställningslänk går till den gamla adressen.',
  },
  {
    id: 'tappat-lappen',
    level: 'buyer',
    question: 'Jag har tappat bort min lapp innan jag hann aktivera den',
    answer:
      'Då är häftet tyvärr borta. Lappen fungerar som en kontantsedel: koden står bara på den, och vi har inget register över vem som fått vilken lapp. Den som hittar den kan aktivera häftet, och vi kan varken spärra koden eller ge dig en ny. Aktivera därför din kod så snart du fått lappen – sedan är häftet knutet till ditt konto och kan inte tas av någon annan.',
  },
  {
    id: 'redan-aktiverad',
    level: 'buyer',
    question: 'Koden säger att den redan är aktiverad, men inte av mig',
    answer:
      'Då har någon annan hunnit aktivera häftet, och det går inte att flytta. Det är samma sak som om lappen tappats bort och någon annan hittat den. Hör av dig till din lagledare så vet föreningen om det – men räkna inte med att häftet går att få tillbaka.',
  },

  /* ---------------------------------------------------------------- *
   * Sälja häften
   * ---------------------------------------------------------------- */
  {
    id: 'registrera-lappar',
    level: 'seller',
    question: 'Jag har fått lappar med koder. Vad gör jag?',
    answer:
      'Registrera dem på dig själv. Då samlas alla dina häften på ett ställe, du ser vilka du delat ut och hur mycket du fått tillbaka, och lagledaren ser vem som har vilka lappar. Lapparna fungerar även oregistrerade – men då finns ingen som håller reda på dem åt dig.',
    steps: [
      'Logga in och gå till Mina häften.',
      'Tryck Registrera häften.',
      'Skriv av koderna, en per rad. Bindestreck och LB- spelar ingen roll.',
      'Skriv gärna spelarens namn, så vet lagledaren vems häften det är.',
    ],
  },
  {
    id: 'registrera-inte-aktivera',
    level: 'seller',
    question: 'Är registrera samma sak som att aktivera?',
    answer:
      'Nej, och det är den viktigaste skillnaden i hela appen. Registrera betyder "de här lapparna har jag och ska sälja" – häftena är fortfarande oanvända. Aktivera gör köparen, när hen betalat dig, och då är häftet i bruk. Aktiverar du ett häfte själv går det inte längre att sälja, och det går inte att ångra.',
  },
  {
    id: 'lappen-upptagen',
    level: 'seller',
    question: 'Det står att koden redan är registrerad av någon annan',
    answer:
      'Kontrollera först att du läst av lappen rätt – koder använder aldrig bokstäverna I, L, O eller U, så en etta är alltid en etta. Stämmer koden har lappen delats ut till någon annan också, och då behöver lagledaren reda ut det.',
  },
  {
    id: 'utan-konto',
    level: 'seller',
    question: 'Måste jag skapa konto för att sälja häften?',
    answer:
      'Nej, du kan sälja lapparna precis som förr. Men med konto ser du dina häften ett och ett, vilka som är utdelade och hur mycket av utlägget som kommit tillbaka. Vill du slippa fixa det själv kan lagledaren registrera lapparna åt dig – då skapas kontot automatiskt och du får ett mail med länk för att välja lösenord.',
  },
  {
    id: 'bestalla-haften',
    level: 'seller',
    question: 'Hur beställer jag häften att sälja?',
    answer:
      'Lagledaren delar en länk till lagets beställningssida. Där fyller du i ditt namn, din e-post, vilken spelare häftena hör till och hur många du vill ha.',
    steps: [
      'Öppna länken du fått av lagledaren.',
      'Fyll i uppgifterna. Antalet är förifyllt med vad föreningen bestämt för laget, men du kan beställa fler.',
      'Du får ett mail med betaluppgifter och en länk till din beställning.',
    ],
  },
  {
    id: 'betala-bestallning',
    level: 'seller',
    question: 'Hur betalar jag?',
    answer:
      'På beställningens sida finns en knapp som öppnar betalningen direkt i mobilen, och en QR-kod för dig som sitter vid datorn. Referensen måste följa med betalningen – det är så föreningen ser att just din betalning kommit in. Står du och skriver in allt för hand finns nummer, belopp och referens som text på samma sida.',
  },
  {
    id: 'far-koderna',
    level: 'seller',
    question: 'När får jag mina koder?',
    answer:
      'När lagledaren bockat av att betalningen kommit in. Då mailas koderna till dig, och de dyker upp under Mina häften. Har du inget konto sedan tidigare skapas ett åt dig och du får välja lösenord i samma mail.',
  },
  {
    id: 'dela-ut-kod',
    level: 'seller',
    question: 'Hur lämnar jag över ett häfte till en köpare?',
    answer:
      'Under Mina häften ser du varje kod för sig. Du kan mejla koden direkt till köparen, eller bara bocka av att du lämnat över den – de flesta häften säljs över köksbordet till en granne som betalar kontant, och då är ett mejl bara i vägen.',
  },
  {
    id: 'pengarna-tillbaka',
    level: 'seller',
    question: 'Hur ser jag hur mycket jag fått tillbaka?',
    answer:
      'Överst på Mina häften står hur mycket av ditt utlägg som kommit tillbaka, räknat på hur många häften du delat ut. Har du köpt fyra häften för 880 kronor och delat ut tre står det 660 av 880 kronor.',
  },
  {
    id: 'angra-utdelning',
    level: 'seller',
    question: 'Jag markerade fel häfte som utdelat',
    answer:
      'Tryck Ångra på raden. Det går så länge köparen inte redan aktiverat koden – har hen gjort det är häftet i bruk och markeringen stämmer.',
  },
  {
    id: 'osalda-haften',
    level: 'seller',
    question: 'Vad gör jag med häften jag inte får sålda?',
    answer:
      'Hör av dig till lagledaren. Koderna slutar fungera när perioden går ut, men fram till dess går de att sälja.',
  },

  /* ---------------------------------------------------------------- *
   * Lagledare
   * ---------------------------------------------------------------- */
  {
    id: 'dela-ut-lappar',
    level: 'teamLeader',
    question: 'Hur delar jag ut de fysiska lapparna?',
    answer:
      'Lapparna kommer från kansliet, tryckta med varsin kod. Dela ut dem som ni alltid gjort – på en träning, i en föräldragrupp. Be varje förälder registrera sina lappar i appen, så vet både de och du vem som har vilka. Registreringen är inte ett krav för att lappen ska fungera, men utan den har du inget underlag att stämma av mot.',
    steps: [
      'Dela ut lapparna och notera vem som fick hur många.',
      'Be föräldrarna registrera dem under Mina häften.',
      'Registrera själv åt dem som inte gör det.',
    ],
  },
  {
    id: 'registrera-at-foralder',
    level: 'teamLeader',
    question: 'Kan jag registrera lappar åt en förälder?',
    answer:
      'Ja. Under Mitt lag finns Registrera lappar åt förälder. Fyll i förälderns e-post, spelarens namn och koderna på lapparna hen fått. Saknas konto skapas ett, och föräldern får ett mail med sina häften och en länk för att välja lösenord. Du kan bara registrera ditt eget lags lappar.',
  },
  {
    id: 'vem-har-vilka-lappar',
    level: 'teamLeader',
    question: 'Hur ser jag vem som har vilka lappar?',
    answer:
      'I kodlistan längst ned står varje kod med sin status. En registrerad lapp är markerad som såld med spelarens namn, men inte som betald – det är två skilda saker. Betalt bockar du av när pengarna kommit in i lagkassan.',
  },
  {
    id: 'registrerad-inte-betald',
    level: 'teamLeader',
    question: 'Varför blir en registrerad lapp inte betald automatiskt?',
    answer:
      'För att registreringen bara säger vem som håller lappen, inte att pengarna kommit in. Föräldrarna betalar oftast i efterhand, när de sålt. Håller du isär de två sakerna vet du hela tiden både vad som är utdelat och vad som är utestående – och det är den andra siffran du behöver jaga.',
  },
  {
    id: 'lagets-lank',
    level: 'teamLeader',
    question: 'Hur får föräldrarna beställa?',
    answer:
      'Under Mitt lag finns lagets egen beställningslänk. Dela den i kanalen ni redan använder – lagchatten, gruppen, ett mejl. Det finns också en QR-kod att hålla upp efter träningen.',
  },
  {
    id: 'bekrafta-betalning',
    level: 'teamLeader',
    question: 'Hur bekräftar jag att någon betalat?',
    answer:
      'I listan Beställningar ser du varje beställning med referens och belopp. Jämför mot lagkassan och tryck Betald. Då delas koderna ut och mejlas till föräldern automatiskt – du behöver inte skicka något själv.',
  },
  {
    id: 'stamma-av-swish',
    level: 'teamLeader',
    question: 'Kan jag bocka av många på en gång?',
    answer:
      'Ja. Ladda ned transaktionsrapporten från Swish och tryck Stäm av mot Swish. Filen läses i din webbläsare och matchas mot referenserna. Innan något bockas av ser du exakt vilka rader som matchar, vilka som har fel belopp och vilka som saknar referens.',
  },
  {
    id: 'fel-belopp',
    level: 'teamLeader',
    question: 'Någon har betalat fel belopp',
    answer:
      'Den raden bockas inte av automatiskt utan visas i rött med både betalt och förväntat belopp. Det är ingen bugg utan ett medvetet val – en betalning på fel summa behöver någon titta på, inte städas bort.',
  },
  {
    id: 'paminna',
    level: 'teamLeader',
    question: 'Någon har beställt men inte betalat',
    answer:
      'Tryck Påminn så går ett vänligt mail ut. Kom ihåg att föräldern inte fått något häfte ännu och alltså inte är skyldig föreningen något – det är en påminnelse, inte ett krav. Det går en påminnelse per dygn och beställning.',
  },
  {
    id: 'avbryta-bestallning',
    level: 'teamLeader',
    question: 'Kan jag ta bort en beställning?',
    answer:
      'Obetalda beställningar går att avbryta. Föräldern kan beställa på nytt när som helst. En betald beställning går inte att avbryta – kontakta kansliet om pengar ska tillbaka.',
  },
  {
    id: 'avstamning-koder',
    level: 'teamLeader',
    question: 'Vad är listan med koder längst ned?',
    answer:
      'Lagets alla koder, förvalt filtrerade på obetalda. Där bockar du av fysiska lappar som sålts kontant. Markerar du flera på en gång visar knappen summan, och en felaktig avbockning går att ångra direkt efteråt.',
  },
  {
    id: 'koder-slut',
    level: 'teamLeader',
    question: 'Laget har slut på koder',
    answer:
      'Be en administratör generera fler under Koder. Antalet som föreslås kommer från lagets åtagande – antal spelare gånger häften per spelare.',
  },
  {
    id: 'digital-forsaljning',
    level: 'teamLeader',
    question: 'Vad är Registrera digital försäljning?',
    answer:
      'Ett snabbspår när du sålt ett häfte direkt till en köpare och redan fått betalt. Koden knyts till köparens e-postadress och mailas dit. Ska en förälder få häften att sälja vidare är beställningslänken rätt väg i stället.',
  },

  /* ---------------------------------------------------------------- *
   * Administratör
   * ---------------------------------------------------------------- */
  {
    id: 'ny-period',
    level: 'admin',
    question: 'Hur startar jag en ny period?',
    answer: 'En period är ett häfte med egna datum, eget pris och eget innehåll.',
    steps: [
      'Häften → Ny period. Fyll i datum, pris och försäljningsmål.',
      'Företag → lägg upp de företag som är med, med logga och kontaktuppgifter.',
      'Erbjudanden → koppla erbjudanden till perioden.',
      'Lag → kontrollera antal spelare, häften per spelare och betaluppgifter.',
      'Koder → generera koder per lag.',
    ],
  },
  {
    id: 'lagga-upp-erbjudande',
    level: 'admin',
    question: 'Hur lägger jag upp ett erbjudande?',
    answer:
      'Under Erbjudanden väljer du företag, rabatttyp och värde. Rabatten formuleras automatiskt av typen, så alla erbjudanden är skrivna likadant. Finstilt text och instruktion till kassören är egna fält.',
  },
  {
    id: 'radera-erbjudande',
    level: 'admin',
    question: 'Hur tar jag bort ett erbjudande?',
    answer:
      'Öppna erbjudandet och tryck Radera längst ned. Du behöver skriva en kort motivering, och raderingen hamnar i loggen. De som redan använt erbjudandet behåller sina inlösen i historiken och statistiken. Vill du bara att det inte ska synas i häftet räcker det att avmarkera Aktivt och spara – då går det att ta tillbaka.',
  },
  {
    id: 'grafisk-profil',
    level: 'admin',
    question: 'Hur sätter jag ett företags grafiska profil?',
    answer:
      'Öppna företaget under Företag. Under Grafisk profil väljer du bakgrund, typsnitt, textstorlek och hur mycket av sidan företaget ska ta, och förhandsvisningen visar hur ett erbjudande kommer att se ut. Profilen gäller alla företagets erbjudanden som inte fått en egen design, och ändrar du den ändras de med den. Utgångsläget är företagets profilfärg, så den som bara fyller i en färg har redan en profil som fungerar.',
  },
  {
    id: 'sidutseende',
    level: 'admin',
    question: 'Kan ett erbjudande se annorlunda ut?',
    answer:
      'Ja, men börja med företaget. Ett företags grafiska profil gäller alla dess erbjudanden, så designen sätts en gång. Ska ett enskilt erbjudande se annorlunda ut väljer du Egen design på erbjudandet – då börjar du från företagets profil och ändrar bara det som ska skilja. Rubriken på sidan är erbjudandets egen och går att skriva om oavsett design.',
  },
  {
    id: 'generera-koder',
    level: 'admin',
    question: 'Hur många koder ska jag generera?',
    answer:
      'Förslaget kommer från lagets åtagande, alltså antal spelare gånger häften per spelare, som du ställer in under Lag. Du kan alltid generera fler i efterhand.',
  },
  {
    id: 'antal-per-spelare',
    level: 'admin',
    question: 'Var ställer jag in hur många häften varje spelare ska sälja?',
    answer:
      'Under Lag, fältet Häften per spelare. Föreningen sätter det utifrån lagets ålder. Summan visas medan du skriver, så du ser vad ändringen betyder i kronor för laget.',
  },
  {
    id: 'betaluppgifter-lag',
    level: 'admin',
    question: 'Var lägger jag in hur laget tar emot betalning?',
    answer:
      'Under Lag finns både Betallänk och Swish-nummer. Finns en länk används den – den pekar på systemet där lagkassan faktiskt bokförs. Utan endera kan laget inte ta emot beställningar, och det syns i listan.',
  },
  {
    id: 'las-statistiken',
    level: 'admin',
    question: 'Hur läser jag statistiken?',
    answer:
      'Statistik har tre flikar. Användning visar när rabatterna används och vilka företag och kategorier som drar – kategorierna sorteras efter inlösen per erbjudande, så att en kategori med många företag inte vinner bara på antalet. Häftena visar vad köparna fått ut av sina häften. Aktivering visar hur det går när lapparna aktiveras. I Användning är grupperingen "Efter aktivering" den mest användbara: den visar hur lång tid efter aktiveringen rabatterna används.',
  },
  {
    id: 'statistik-foretag',
    level: 'admin',
    question: 'Hur ser jag hur ett enskilt företag går?',
    answer:
      'Välj företaget i listan överst på Statistik, eller tryck på det under Toppföretag. Då syns hur många olika häften som använt företaget och hur stor andel av alla aktiverade häften det är, varje erbjudande för sig – även de som ingen använt – och vilka dagar och tider kunderna kommer. Överst står det viktigaste i klartext, så att det går att läsa upp i ett samtal med företaget. Visa aldrig enskilda inlösen för företaget, och var försiktig med staplar som bara har ett par inlösen: på en liten ort kan de peka ut en person.',
  },
  {
    id: 'statistik-haftena',
    level: 'admin',
    question: 'Vad betyder siffrorna under Häftena?',
    answer:
      '"Har betalat sig" är andelen aktiverade häften som sparat minst vad häftet kostade – den bästa siffran att sälja nästa häfte med. "Oanvända häften" är aktiverade häften utan en enda inlösen; de som legat så i mer än 30 dagar är värda en påminnelse. "Betalda, inte aktiverade" är lappar lagledaren bockat av men som köparen aldrig aktiverat. Tabellen per lag visar var det stannar upp. Siffrorna räknas om varje natt, och med Räkna om nu när du vill se dem direkt.',
  },
  {
    id: 'statistik-aktivering',
    level: 'admin',
    question: 'Hur vet jag om de tryckta lapparna fungerar?',
    answer:
      'Titta under Statistik → Aktivering. Där räknas varje försök att aktivera en kod: hur många som lyckas, hur många som kommer via QR-koden och hur många som skriver fel. När det går att se vilken kod som menades visas vilka tecken som förväxlas – dyker samma par upp om och om igen, till exempel 8 och B, är typsnittet på lappen för otydligt. Misslyckas försök via QR-koden är något fel med länken, och då ska du höra av dig till tryckeriet direkt. Inget sparas om vem som försökte.',
  },
  {
    id: 'ladda-ned-underlag',
    level: 'admin',
    question: 'Kan jag få ut rådata?',
    answer:
      'Ja, knappen Ladda ned underlag på Statistik ger en CSV med varje inlösen: datum, klockslag, veckodag, erbjudande, företag, värde och kvittonummer. Filen öppnas direkt i Excel.',
  },
  {
    id: 'roller',
    level: 'admin',
    question: 'Vem får göra vad?',
    answer:
      'Köpare använder sitt häfte. Lagledare ser sitt eget lag. Administratör lägger upp innehåll och ser alla lag. Superadministratör sköter roller, testdata och rättelser. Roller ändras under Användare och kräver superadministratör.',
  },
  {
    id: 'rapporter',
    level: 'admin',
    question: 'Vad är Rapporter?',
    answer:
      'Buggar och förslag som användarna skickat in härifrån. Du sätter status och kan skriva en intern anteckning som sparas när du klickar utanför fältet.',
  },

  /* ---------------------------------------------------------------- *
   * Superadministratör
   * ---------------------------------------------------------------- */
  {
    id: 'lasttest',
    level: 'superadmin',
    question: 'Hur testar jag att appen klarar många användare?',
    answer:
      'Under Test finns Lasttest, när det finns ett testhäfte. Lunchen låter påhittade köpare lösa in två rabatter var, med varannan inlösen på samma rabatt. Föräldramötet låter dem aktivera var sin kod på en gång. Båda går genom exakt samma steg som en riktig köpare, men inga konton skapas och föreningens siffror påverkas inte. Svaret visas som bock eller kryss per sak som ska hålla. Rensa testdatan efteråt.',
  },
  {
    id: 'testdata',
    level: 'superadmin',
    question: 'Vad gör Skapa testhäfte?',
    answer:
      'Skapar ett komplett provhäfte med tolv företag, trettio erbjudanden, åtta lag och sexhundra koder – varav de flesta aktiverade och betalda – plus flera tusen inlösen spridda över ett halvår. Allt märks TEST och räknas inte in i årsstatistiken. Det tar ungefär tio sekunder.',
  },
  {
    id: 'rensa-testdata',
    level: 'superadmin',
    question: 'Är det säkert att rensa testdatan?',
    answer:
      'Ja. Rensningen tar bort exakt det som är märkt TEST och ingenting annat, och en förhandsvisning räknar upp vad som försvinner innan du bekräftar. Ett halvfärdigt riktigt häfte som väntar på nästa period rörs inte.',
  },
  {
    id: 'aterstalla-inlosen',
    level: 'superadmin',
    question: 'Kan jag ångra en inlöst rabatt åt någon?',
    answer:
      'Ja, längst ned på Logg. Det är undantaget från regeln att en använd rabatt är använd, och det ska vara sällsynt – använd det när något uppenbart gått fel i kassan. Din motivering sparas i loggen, och köparen får tillbaka uttaget.',
  },
  {
    id: 'backup',
    level: 'superadmin',
    question: 'Hur fungerar backupen?',
    answer:
      'En automatisk säkerhetskopia tas varje natt och sparas i trettio dagar. Du kan också ta en direkt från Översikt. Kopiorna innehåller hela databasen och ligger i föreningens eget lagringsutrymme.',
  },
  {
    id: 'radera-konto',
    level: 'superadmin',
    question: 'En medlem vill bli raderad',
    answer:
      'Användaren kan radera sitt eget konto under Konto. Inlösta rabatter ligger kvar som anonym statistik – de säger vad som hände i butiken, inte vem som gjorde det.',
  },
  {
    id: 'loggen',
    level: 'superadmin',
    question: 'Vad hamnar i loggen?',
    answer:
      'Allt administratörer gör som rör häftet: nya företag och erbjudanden, genererade koder, rollbyten, bekräftade betalningar och återställda inlösen. Loggen går inte att ändra från appen.',
  },
]

/** Nivåerna en viss roll ska se, i den ordning de visas. */
export function levelsForRole(role: Role): HelpLevel[] {
  const base: HelpLevel[] = ['buyer', 'seller']
  if (role === 'teamLeader') return [...base, 'teamLeader']
  if (role === 'admin') return [...base, 'teamLeader', 'admin']
  if (role === 'superadmin') return [...base, 'teamLeader', 'admin', 'superadmin']
  return base
}

/**
 * Fritextsökning över frågor, svar och steg.
 *
 * Enkel delsträngsmatchning med normaliserade tecken. Den som söker "swish"
 * ska hitta avstämningen även om ordet står mitt i en mening, och "aterstalla"
 * ska hitta "återställa".
 */
export function searchHelp(topics: HelpTopic[], needle: string): HelpTopic[] {
  const query = normalize(needle)
  if (!query) return topics
  return topics.filter((topic) =>
    normalize([topic.question, topic.answer, ...(topic.steps ?? [])].join(' ')).includes(query),
  )
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
}

/* ------------------------------------------------------------------ *
 * Hjälp där man står
 * ------------------------------------------------------------------ */

/**
 * Vilka avsnitt som hör till vilken sida.
 *
 * Ligger som en egen karta i stället för ett fält på varje avsnitt. Skälet är
 * att den går att läsa i ett svep: frågan "vad får en lagledare hjälp med på
 * lagsidan?" besvaras av en rad, inte av att man scannar fyrtio avsnitt efter
 * en tagg.
 *
 * Nycklarna matchas som prefix och längsta träffen vinner, så /admin/koder
 * hittar sin egen rad medan /admin/nagot-nytt faller tillbaka på /admin.
 */
const PAGE_TOPICS: Record<string, string[]> = {
  '/': ['anvanda-rabatt', 'angra-rabatt', 'flera-haften', 'offline', 'personalen-fragar', 'haftet-gar-ut'],
  '/foretag': ['anvanda-rabatt', 'personalen-fragar', 'flera-haften'],
  // Lapparna först: en förälder utan registrerade häften landar här, och
  // det är här knappen för att registrera dem sitter.
  '/mina-koder': [
    'registrera-lappar',
    'aktivera-kod',
    'registrera-inte-aktivera',
    'byta-enhet',
    'flera-haften',
  ],
  '/mina-haften': [
    'registrera-lappar',
    'registrera-inte-aktivera',
    'dela-ut-kod',
    'pengarna-tillbaka',
    'angra-utdelning',
    'lappen-upptagen',
    'osalda-haften',
    'far-koderna',
  ],
  '/aktivera': ['aktivera-kod', 'registrera-inte-aktivera', 'byta-enhet'],
  '/bestall': ['bestalla-haften', 'betala-bestallning', 'far-koderna', 'dela-ut-kod'],
  '/konto': ['byta-enhet', 'utan-konto', 'radera-konto'],

  '/lag': [
    'dela-ut-lappar',
    'registrera-at-foralder',
    'vem-har-vilka-lappar',
    'registrerad-inte-betald',
    'lagets-lank',
    'bekrafta-betalning',
    'stamma-av-swish',
    'fel-belopp',
    'paminna',
    'avbryta-bestallning',
    'avstamning-koder',
    'koder-slut',
    'digital-forsaljning',
  ],

  '/admin': ['ny-period', 'las-statistiken', 'roller'],
  '/admin/test': ['testdata', 'lasttest', 'rensa-testdata', 'backup'],
  '/admin/statistik': [
    'las-statistiken',
    'statistik-foretag',
    'statistik-haftena',
    'statistik-aktivering',
    'ladda-ned-underlag',
  ],
  '/admin/haften': ['ny-period', 'antal-per-spelare'],
  '/admin/foretag': ['grafisk-profil', 'lagga-upp-erbjudande', 'ny-period'],
  '/admin/erbjudanden': ['lagga-upp-erbjudande', 'sidutseende', 'grafisk-profil', 'radera-erbjudande'],
  '/admin/lag': ['antal-per-spelare', 'betaluppgifter-lag', 'generera-koder'],
  '/admin/koder': ['generera-koder', 'antal-per-spelare', 'koder-slut'],
  '/admin/anvandare': ['roller'],
  '/admin/rapporter': ['rapporter'],
  '/admin/logg': ['loggen', 'aterstalla-inlosen'],
}

/**
 * Avsnitten som hör till en sida, filtrerade på vad rollen får se.
 *
 * Rollfiltret är inte kosmetiskt. En lagledare som står på lagsidan ska inte
 * få hjälp om backup, och en köpare som råkar hamna på en adminsida ska inte
 * läsa hur man genererar koder.
 */
/**
 * Avsnitt som bara gäller när en funktion är påslagen.
 *
 * Hjälp om något som inte finns är värre än ingen hjälp: den som läser går
 * och letar efter en knapp som aldrig kommer, och ringer sedan kansliet.
 * Listan är därför per avsnitt och inte per nivå – samma sida blandar
 * påslaget och avstängt.
 */
const TOPIC_FEATURE: Record<string, FeatureKey> = {
  // Digital beställning
  'bestalla-haften': 'digitalOrders',
  'betala-bestallning': 'digitalOrders',
  'far-koderna': 'digitalOrders',
  'lagets-lank': 'digitalOrders',
  'bekrafta-betalning': 'digitalOrders',
  'stamma-av-swish': 'digitalOrders',
  'fel-belopp': 'digitalOrders',
  paminna: 'digitalOrders',
  'avbryta-bestallning': 'digitalOrders',

  // Registrering av fysiska lappar
  'registrera-lappar': 'slipRegistration',
  'registrera-inte-aktivera': 'slipRegistration',
  'lappen-upptagen': 'slipRegistration',
  'utan-konto': 'slipRegistration',
  'dela-ut-kod': 'slipRegistration',
  'pengarna-tillbaka': 'slipRegistration',
  'angra-utdelning': 'slipRegistration',
  'dela-ut-lappar': 'slipRegistration',
  'registrera-at-foralder': 'slipRegistration',
  'vem-har-vilka-lappar': 'slipRegistration',
  'registrerad-inte-betald': 'slipRegistration',

  // Digital försäljning från lagvyn
  'digital-forsaljning': 'digitalSale',
}

/** Går avsnittet att visa med de här flaggorna? */
export function topicEnabled(topic: HelpTopic, features?: Partial<Features>): boolean {
  const key = TOPIC_FEATURE[topic.id]
  if (!key) return true
  // Utan uppgift om flaggorna visas allt – hjälpsidan ska aldrig bli tom bara
  // för att någon glömt skicka med dem.
  if (!features) return true
  return features[key] === true
}

export function helpForPage(
  pathname: string,
  role: Role,
  features?: Partial<Features>,
): HelpTopic[] {
  const levels = levelsForRole(role)

  // Längsta matchande prefix vinner: /admin/koder före /admin.
  const key = Object.keys(PAGE_TOPICS)
    .filter((path) => pathname === path || pathname.startsWith(path === '/' ? '\u0000' : path))
    .sort((a, b) => b.length - a.length)[0]

  const ids = PAGE_TOPICS[key ?? ''] ?? (pathname === '/' ? PAGE_TOPICS['/'] : [])

  return ids
    .map((id) => HELP_TOPICS.find((topic) => topic.id === id))
    .filter((topic): topic is HelpTopic => topic !== undefined && levels.includes(topic.level))
    .filter((topic) => topicEnabled(topic, features))
}
